/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/helpers';
import {ApplicationIdsFilters} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {PageState} from '@/types/pages';
import {PageInitializeAction} from '@/types/redux';
import {gridDefaultPageSize} from '../../../utils/helpers';

// default region for ApplicationIds Devices
export const DEFAULT_REGION                        = 'ApplicationIds';

// name of reducer
export const REDUCER_NAME                          = 'applicationIds';

export const INITIALIZE_APPLICATION_IDS            = 'INITIALIZE_APPLICATION_IDS';
export const SET_GRID_COLUMNS                      = 'SET_APPLICATION_IDS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                   = 'UPDATE_APPLICATION_IDS_GRID_COLUMNS';
export const APPLICATION_IDS_GRID_REFRESHED        = 'APPLICATION_IDS_GRID_REFRESHED';
export const SET_GRID_PAGE_SIZE                    = 'SET_APPLICATION_IDS_GRID_PAGE_SIZE';

// search queries
export const UPDATE_APPLICATION_IDS_SEARCH_QUERIES = 'UPDATE_APPLICATION_IDS_SEARCH_QUERIES';
export const RESET_APPLICATION_IDS_SEARCH_QUERIES  = 'RESET_APPLICATION_IDS_SEARCH_QUERIES';

/**
 * initialize ApplicationIds
 */
export const initializeApplicationIds = (): PageInitializeAction => {
  return {type: INITIALIZE_APPLICATION_IDS};
};

/**
 * Invoked when Grid Refreshed
 */
export const applicationIdsGridRefreshed = (): PageInitializeAction => {
  return {type: APPLICATION_IDS_GRID_REFRESHED};
};

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setApplicationIdsGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

// Updates the modular search queries for ApplicationIds
export const updateApplicationIdsSearchQueries = (searchParams: ApplicationIdsFilters) => ({
  type : UPDATE_APPLICATION_IDS_SEARCH_QUERIES, searchParams
});

// Resets the ApplicationIds filters state
export const resetApplicationIdsSearchQueries = () => ({
  type : RESET_APPLICATION_IDS_SEARCH_QUERIES
});

/**
 * REDUCERS
 */
export interface ApplicationIdsState extends PageState {
  applicationIds: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    pageSize: number
  },
  search: ApplicationIdsFilters
};

const clearSearch = (): ApplicationIdsFilters => ({
  manufacturer   : null,
  model          : null,
  microprocessor : null,
  cellularModem  : null,
  deviceFamily   : null,
  carrier        : null,
  status         : null,
  createdOn      : null
});

// defines the user preference key for the application ids page ...
export const PREFERENCE = 'applicationIds';

// defines the initial state for the reducer ...
export const initialState: ApplicationIdsState = {
  isInitialized  : false,
  applicationIds : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    pageSize            : gridDefaultPageSize
  },
  search : clearSearch()
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_APPLICATION_IDS] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_GRID_COLUMNS] : ({grid, applicationIds}: ApplicationIdsState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : applicationIds.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: ApplicationIdsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: ApplicationIdsState, {pageSize}) => {
    grid.pageSize = pageSize;
  },
  [UPDATE_APPLICATION_IDS_SEARCH_QUERIES] : ({search}: ApplicationIdsState, {searchParams}) => {
    search.manufacturer   = searchParams?.manufacturer || null;
    search.model          = searchParams?.model || null;
    search.microprocessor = searchParams?.microprocessor || null;
    search.cellularModem  = searchParams?.cellularModem || null;
    search.deviceFamily   = searchParams?.deviceFamily || null;
    search.carrier        = searchParams?.carrier || null;
    search.status         = searchParams?.status || null;
    search.createdOn      = searchParams?.createdOn || null;
  },
  [RESET_APPLICATION_IDS_SEARCH_QUERIES] : draft => {
    draft.search = clearSearch();
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: ApplicationIdsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
